<template>
  <div class="step-container">
    <!-- Header Section -->
    <StepHeader :currentStep="currentStep" :steps="steps" />

    <!-- Content Section -->
    <div class="content-section">
      <!-- Export Section -->
      <div class="export-section">
        <button class="export-button" @click="exportConfiguration">
          <svg class="export-icon" fill="none" viewBox="0 0 24 24">
            <path
              d="M11.5 15.577V6.927L9.17 9.257L8.462 8.539L12 5L15.539 8.539L14.831 9.258L12.5 6.927V15.577H11.5ZM5 19V14.962H6V18H18V14.962H19V19H5Z"
              fill="currentColor"
            ></path>
          </svg>
          <span>Export</span>
        </button>
      </div>

      <!-- Summary Table -->
      <div class="summary-table-section">
        <div class="wizard-table-container">
          <div class="wizard-table-body">
            <div class="wizard-table-row">
              <div class="wizard-table-cell">{{ lang.W00075 }}</div>
              <div class="wizard-table-cell">{{ formData.waveName }}</div>
            </div>
            <div class="wizard-table-row">
              <div class="wizard-table-cell">{{ lang.W00016 }}</div>
              <div class="wizard-table-cell">{{ formData.surveyTitle }}</div>
            </div>
            <div class="wizard-table-row">
              <div class="wizard-table-cell">{{ lang.W00027 }}</div>
              <div class="wizard-table-cell">{{ formData.importerName }}</div>
            </div>
            <div class="wizard-table-row">
              <div class="wizard-table-cell">{{ lang.W00029 }}</div>
              <div class="wizard-table-cell">{{ formData.agencyName }}</div>
            </div>
            <div class="wizard-table-row">
              <div class="wizard-table-cell">{{ lang.W00076 }}</div>
              <div class="wizard-table-cell">{{ formData.leadAgency }}</div>
            </div>
            <div class="wizard-table-row">
              <div class="wizard-table-cell">{{ lang.W00077 }}</div>
              <div class="wizard-table-cell">{{ scenariosNames }}</div>
            </div>
            <div class="wizard-table-row">
              <div class="wizard-table-cell">Audi {{ lang.W00078 }}</div>
              <div class="wizard-table-cell">{{ carModelDistributionDisplay }}</div>
            </div>
            <div class="wizard-table-row" v-for="brand in competitorBrands">
              <div class="wizard-table-cell">{{ brand }} {{ lang.W00078 }}</div>
              <div class="wizard-table-cell">{{ competitorCarModelDistributionDisplay[brand].join(", ") }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Visits table -->
      <div class="summary-table-section">
        <div class="wizard-table-container">
          <div class="wizard-table-header wizard-table-visits-header">
            <div>{{ lang.SA0015 }}</div>
            <div>{{ lang.SA0044 }}</div>
            <div>Checks</div>
            <div>{{ lang.CM1034 }}</div>
            <div>{{ lang.CM1035 }}</div>
          </div>
          <div class="wizard-table-body">
            <div
              class="wizard-table-row wizard-table-visits-row"
              v-for="visit in formData.visits || []"
              :key="`${visit.LocationId}-${visit.ScenarioName}`"
            >
              <div class="wizard-table-cell">{{ visit.LocationName }}</div>
              <div class="wizard-table-cell">{{ visit.ScenarioName }}</div>
              <div class="wizard-table-cell">{{ visit.NumberOfVisits }}</div>
              <div class="wizard-table-cell">{{ getAttachmentFileName(visit.ScenarioId, 'Briefing') }}</div>
              <div class="wizard-table-cell">{{ getAttachmentFileName(visit.ScenarioId, 'Survey') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "../../../styles/audi-steps.css";
import StepHeader from "./StepHeader.vue";
import wizardService from "../../../services/WizardService";

export default {
  name: "Step8",
  components: {
    StepHeader,
  },
  props: {
    formData: { type: Object, required: true },
    currentStep: { type: Number, default: 0 },
    steps: { type: Array, required: true },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      lang: {
        W00000: "Configuration Overview",
        W00075: "Wave",
        W00016: "Survey",
        W00027: "Importer",
        W00029: "Local agency",
        W00076: "Lead agency",
        W00077: "Scenarios",
        W00078: "distribution",
        CM1034: "Briefing",
        CM1035: "Survey",
        SA0015: "Location",
        SA0044: "Scenario",
      },
    };
  },
  computed: {
    scenariosNames() {
      const sc = this.formData && this.formData.scenarios;
      if (!sc || !Array.isArray(sc) || sc.length === 0) return "";
      return sc
        .map((s) => s.ScenarioName || s.name || "")
        .filter(Boolean)
        .join(", ");
    },
    carModelDistributionDisplay() {
      const models = this.formData && this.formData.carModelDistribution;
      if (!models || !Array.isArray(models) || models.length === 0) return "";
      return models
        .map((m) => `${m.CarModelName}: ${m.Distribution * 100}%`)
        .join(", ");
    },
    competitorCarModelDistributionDisplay() {
      const competitors =
        this.formData && this.formData.competitorCarModelDistribution;
      if (
        !competitors ||
        !Array.isArray(competitors) ||
        competitors.length === 0
      )
        return {};

      const grouped = {};
      competitors.forEach((c) => {
        if (!grouped[c.CompetitorCarBrand]) {
          grouped[c.CompetitorCarBrand] = [];
        }
        grouped[c.CompetitorCarBrand].push(
          `${c.CompetitorCarModel}: ${parseFloat(c.Distribution) * 100}%`
        );
      });

      return grouped;
    },
    competitorBrands() {
      return Object.keys(this.competitorCarModelDistributionDisplay);
    },
  },
  watch: {
    "globals.selectedLanguage"() {
      this.globals.getLanguageStrings(this);
    },
  },
  mounted() {
    if (this.globals.selectedLanguage) {
      this.globals.getLanguageStrings(this);
    }
  },
  methods: {
    validateStep() {
      return { valid: true, msg: null };
    },
    exportConfiguration() {
      wizardService.exportConfiguration(this.formData.configurationGuid);
    },
    getAttachmentFileName(scenarioId, type) {
      const attachments = this.formData && this.formData.briefing;
      if (!attachments || !Array.isArray(attachments)) return "";
      const found = attachments.find(a => a.ScenarioId === scenarioId && a.AttachmentType === type);
      return found ? found.DownloadFileName : "";
    },
  },
};
</script>

<style lang="css" scoped>
.wizard-table-header {
  grid-template-columns: 1fr 2fr;
}
.wizard-table-visits-header {
  grid-template-columns: 1fr 2fr 1fr 1fr 1fr !important;
}
.wizard-table-row {
  grid-template-columns: 1fr 2fr;
}
.wizard-table-visits-row {
  grid-template-columns: 1fr 2fr 1fr 1fr 1fr !important;
}
.summary-table-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.export-section {
  display: flex;
  flex-direction: row;
}

.export-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 150px;
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #333333;
  }

  &:focus-visible {
    outline: none;
    border: 3px solid rgba(0, 0, 0, 0.5);
  }
}
.export-icon {
  width: 20px;
  height: 20px;
}
</style>



// WEBPACK FOOTER //
// Step8.vue?017cd552